<script lang="ts">
  import Icon from '../components/Icon.svelte';
  import { BRAND_NAME } from '../lib/brand';
  import { onMount } from 'svelte';
  import { replace } from 'svelte-spa-router';
  import TopBar from '../components/TopBar.svelte';
  import { session } from '../lib/session';
  import { api, ApiError } from '../lib/api';
  import { formatDate, daysWord, pluralUa } from '../lib/format';
  import { getTelegramWebApp } from '../lib/telegram';
  import type { ReferralEarningDTO, ReferralMeDTO } from '../lib/types';

  let info = $state<ReferralMeDTO | null>(null);
  let earnings = $state<ReferralEarningDTO[]>([]);
  let nextCursor = $state<string | null>(null);
  let loading = $state(true);
  let loadingMore = $state(false);
  let error = $state<string | null>(null);
  let copied = $state(false);

  // Посилання будуємо з поточного origin, а не на бекенді: сервер не знає
  // публічного домену напевно, а браузер — знає завжди, і в Telegram WebView
  // це наш же домен.
  const link = $derived(info ? `${window.location.origin}/r/${info.code}` : '');

  const STATUS: Record<ReferralEarningDTO['status'], { text: string; cls: string }> = {
    available: { text: 'Доступно', cls: 'green' },
    pending: { text: 'В очікуванні', cls: 'yellow' },
    reversed: { text: 'Скасовано', cls: 'red' },
  };

  async function copyLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // Telegram WebView подекуди не дає clipboard API — старий шлях через
      // приховане поле працює там, де новий мовчки відмовляє.
      const el = document.createElement('textarea');
      el.value = link;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand('copy');
      } finally {
        document.body.removeChild(el);
      }
    }
    copied = true;
    setTimeout(() => (copied = false), 1800);
  }

  function share(): void {
    const text = `Приєднуйся до ${BRAND_NAME} за моїм посиланням:\n${link}`;
    const tg = getTelegramWebApp();
    // switchInlineQuery не годиться (працює лише для inline-режиму бота), тож
    // відкриваємо стандартний діалог пересилання Telegram.
    const url = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(`Приєднуйся до ${BRAND_NAME}`)}`;
    if (tg && 'openTelegramLink' in tg && typeof tg.openTelegramLink === 'function') {
      (tg as unknown as { openTelegramLink: (u: string) => void }).openTelegramLink(url);
      return;
    }
    if (navigator.share) {
      void navigator.share({ text }).catch(() => {});
      return;
    }
    window.open(url, '_blank', 'noopener');
  }

  async function loadMore(): Promise<void> {
    if (!nextCursor || loadingMore) return;
    loadingMore = true;
    try {
      const res = await api.referralEarnings(nextCursor);
      earnings = [...earnings, ...res.items];
      nextCursor = res.nextCursor;
    } catch {
      /* мовчки: список уже показаний, довантаження — необовʼязкове */
    } finally {
      loadingMore = false;
    }
  }

  onMount(async () => {
    if (!$session) {
      replace(import.meta.env.DEV ? '/' : '/login');
      return;
    }
    // Рефералка прихована в цьому середовищі — прямий перехід за адресою теж закритий.
    if (!$session.referralsVisible) {
      replace('/profile');
      return;
    }
    try {
      info = await api.referralMe();
      const res = await api.referralEarnings();
      earnings = res.items;
      nextCursor = res.nextCursor;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        replace(import.meta.env.DEV ? '/' : '/login');
        return;
      }
      error = 'Не вдалось завантажити дані';
    } finally {
      loading = false;
    }
  });
</script>

<div class="screen">
  <TopBar title="Реферальна система" backHref="/profile" />
  <div class="scroll">
    {#if loading}
      <div class="placeholder">Завантажуємо…</div>
    {:else if !info}
      <div class="placeholder error">{error ?? 'Не вдалось завантажити дані'}</div>
    {:else}
      <!-- Баланс: два числа, бо нарахування спершу проходить період утримання -->
      <div class="balance-card">
        <div class="balance-main">
          <div class="balance-label">Доступно зараз</div>
          <div class="balance-value">{info.available.toFixed(2)} ₴</div>
        </div>
        <div class="balance-side">
          <div class="balance-row">
            <span>В очікуванні</span><strong>{info.pending.toFixed(2)} ₴</strong>
          </div>
          <div class="balance-row">
            <span>Всього нараховано</span><strong>{info.totalEarned.toFixed(2)} ₴</strong>
          </div>
          {#if info.spent > 0}
            <div class="balance-row">
              <span>Витрачено</span><strong>{info.spent.toFixed(2)} ₴</strong>
            </div>
          {/if}
        </div>
      </div>
      {#if info.pending > 0}
        <div class="note">
          Бонус стає доступним через {info.holdDays}
          {daysWord(info.holdDays)} після оплати — це строк, протягом якого
          можливе повернення коштів.
        </div>
      {/if}

      <div class="section-title">Твоє посилання</div>
      <div class="link-box">{link}</div>
      <div class="link-actions">
        <button type="button" class="btn primary" onclick={copyLink}>
          {#if copied}<Icon name="check" size="xs" /> Скопійовано{:else}Скопіювати{/if}
        </button>
        <button type="button" class="btn" onclick={share}>Поділитись</button>
      </div>

      <div class="stat-grid">
        <div class="stat">
          <div class="stat-value">{info.clicks}</div>
          <div class="stat-label">{pluralUa(info.clicks, 'натискання', 'натискання', 'натискань')}</div>
        </div>
        <div class="stat">
          <div class="stat-value">{info.uniqueVisitors}</div>
          <div class="stat-label">{pluralUa(info.uniqueVisitors, 'унікальний', 'унікальні', 'унікальних')}</div>
        </div>
        <div class="stat">
          <div class="stat-value">{info.joined}</div>
          <div class="stat-label">{pluralUa(info.joined, 'приєдналась', 'приєдналось', 'приєдналось')}</div>
        </div>
      </div>

      <div class="section-title">Умови</div>
      <div class="terms">
        {#if info.termsMarkdown.trim()}
          {info.termsMarkdown}
        {:else}
          Ти отримуєш <strong>{info.percent}%</strong> з першої покупки кожної людини,
          яка прийшла за твоїм посиланням. Комісія нараховується один раз — з покупки
          продукту, якого ця людина раніше не купувала, якщо вона оплатить протягом
          {info.purchaseWindowDays} {daysWord(info.purchaseWindowDays)} після переходу.
          Бонусами можна оплачувати власні покупки.
        {/if}
      </div>

      <div class="section-title">Поповнення</div>
      {#if earnings.length === 0}
        <div class="placeholder">
          Поки що порожньо. Поділись посиланням — нарахування зʼявляться тут одразу
          після першої покупки запрошеної людини.
        </div>
      {:else}
        <div class="earn-list">
          {#each earnings as e (e.id)}
            <div class="earn-item" class:reversed={e.status === 'reversed'}>
              <div class="earn-head">
                <div class="earn-name">{e.name}</div>
                <div class="earn-amount">+{e.amount.toFixed(2)} ₴</div>
              </div>
              <div class="earn-meta">
                <span>Оплата {e.paid.toFixed(2)} ₴ · {e.percent}%</span>
                <span class="earn-status {STATUS[e.status].cls}">{STATUS[e.status].text}</span>
              </div>
              <div class="earn-date">{formatDate(e.occurredAt)}</div>
            </div>
          {/each}
        </div>
        {#if nextCursor}
          <button type="button" class="btn more" onclick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Завантажуємо…' : 'Показати ще'}
          </button>
        {/if}
      {/if}
    {/if}
  </div>
</div>

<style>
  .screen {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: var(--bg);
  }
  .scroll {
    flex: 1;
    overflow-y: auto;
    padding: 18px 18px 40px;
  }
  .scroll::-webkit-scrollbar {
    width: 3px;
  }
  .scroll::-webkit-scrollbar-thumb {
    background: var(--border2);
    border-radius: 2px;
  }

  .placeholder {
    padding: 16px;
    text-align: center;
    color: var(--text-muted);
    font-size: 12px;
    line-height: 1.5;
    background: var(--bg2);
    border: 1px dashed var(--border2);
    border-radius: 10px;
  }
  .placeholder.error {
    color: var(--status-inactive);
  }

  .balance-card {
    background: linear-gradient(135deg, var(--dark3), var(--pu3));
    border-radius: 14px;
    padding: 16px;
    color: var(--text-light);
    margin-bottom: 10px;
  }
  .balance-label {
    font-size: 11px;
    opacity: 0.75;
  }
  .balance-value {
    font-size: 28px;
    font-weight: 700;
    margin-top: 2px;
  }
  .balance-side {
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid rgba(251, 246, 236, 0.16);
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .balance-row {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    opacity: 0.9;
  }
  .balance-row strong {
    font-weight: 600;
  }

  .note {
    font-size: 11px;
    color: var(--text-muted);
    line-height: 1.45;
    margin-bottom: 16px;
    padding: 0 2px;
  }

  .section-title {
    font-size: 12px;
    font-weight: 700;
    color: var(--text);
    margin: 18px 0 10px;
  }

  .link-box {
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 12px 13px;
    font-size: 12.5px;
    color: var(--text);
    word-break: break-all;
    line-height: 1.4;
  }
  .link-actions {
    display: flex;
    gap: 8px;
    margin-top: 8px;
  }
  .btn {
    flex: 1;
    padding: 12px;
    border-radius: 11px;
    border: 1px solid var(--border2);
    background: var(--bg);
    color: var(--text);
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
  }
  .btn.primary {
    border: none;
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    color: var(--text-light);
  }
  .btn.more {
    margin-top: 10px;
    width: 100%;
  }
  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .stat-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-top: 14px;
  }
  .stat {
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 11px;
    padding: 12px 8px;
    text-align: center;
  }
  .stat-value {
    font-size: 19px;
    font-weight: 700;
    color: var(--text);
  }
  .stat-label {
    font-size: 10px;
    color: var(--text-muted);
    margin-top: 3px;
  }

  .terms {
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 13px 14px;
    font-size: 12px;
    line-height: 1.55;
    color: var(--text);
    white-space: pre-line;
  }
  .terms strong {
    color: var(--accent-text);
    font-weight: 700;
  }

  .earn-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .earn-item {
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 12px 14px;
  }
  .earn-item.reversed {
    opacity: 0.6;
  }
  .earn-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 10px;
  }
  .earn-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .earn-amount {
    flex-shrink: 0;
    font-size: 14px;
    font-weight: 700;
    color: var(--accent-text);
  }
  .earn-item.reversed .earn-amount {
    color: var(--text-muted);
    text-decoration: line-through;
  }
  .earn-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    margin-top: 5px;
    font-size: 11px;
    color: var(--text-muted);
  }
  .earn-status {
    flex-shrink: 0;
    font-weight: 600;
    padding: 3px 9px;
    border-radius: 999px;
    background: var(--bg);
  }
  .earn-status.green {
    color: var(--status-active);
    background: var(--green-light, var(--green-light));
  }
  .earn-status.yellow {
    color: var(--status-expiring);
    background: var(--warn-bg);
  }
  .earn-status.red {
    color: var(--status-inactive);
    background: var(--danger-bg);
  }
  .earn-date {
    font-size: 10.5px;
    color: var(--text-muted);
    margin-top: 5px;
  }
</style>
